import { RefObject, useEffect, useMemo, useRef } from 'react';
import { getFocusableTreeWalker } from '@react-aria/focus';

import { getDocumentBody } from '../../../dom/getDocumentBody/getDocumentBody';

/**
 * Определяет лежит ли element в scope
 *
 * Взято из react-aria https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/focus/src/FocusScope.tsx#L410
 *
 * @param element Элемент, который проверяем на наличие в scope
 * @param scope Scope, внутри которого ищем element
 * @returns true, если element находится в scope
 */
function isElementInScope(element?: Element | null, scope?: Element[] | null) {
    if (!element) {
        return false;
    }
    if (!scope) {
        return false;
    }
    return scope.some(node => node.contains(element));
}

/**
 * Достает родительский элемент скоупа
 *
 * Взято из react-aria https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/focus/src/FocusScope.tsx#L281
 *
 * @param scope элементы внутри FocusScope
 * @returns Родительский элемент скоупа
 */
function getScopeRoot(scope: Element[]) {
    return scope[0]?.parentElement;
}

/**
 * Перекидывает фокус на первый элемент в FocusScope
 *
 * Взято из react-aria https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/focus/src/FocusScope.tsx#L469
 *
 * @param scope элементы внутри FocusScope
 * @param tabbable boolean, определяющий должен ли быть доступным по табу первый элемент для фокуса
 * @returns
 */
function focusFirstInScope(props: { scope: Element[]; tabbable?: boolean }) {
    const { scope, tabbable = true } = props;
    const sentinel = scope[0]?.previousElementSibling;
    let scopeRoot = getScopeRoot(scope);
    if (!scopeRoot || !sentinel) {
        return;
    }

    let walker = getFocusableTreeWalker(scopeRoot, { tabbable }, scope);
    walker.currentNode = sentinel;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions --- необходимо для вызова focus
    let nextNode = walker.nextNode() as HTMLElement | null;

    // If the scope does not contain a tabbable element, use the first focusable element.
    if (tabbable && !nextNode) {
        scopeRoot = getScopeRoot(scope);
        if (!scopeRoot) {
            return;
        }
        walker = getFocusableTreeWalker(scopeRoot, { tabbable: false }, scope);
        walker.currentNode = sentinel;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions --- необходимо для вызова focus
        nextNode = walker.nextNode() as HTMLElement | null;
    }

    nextNode?.focus({
        preventScroll: true,
    });
}

/**
 * В эффекте вызывает фокус на первый элемент оверлея, при изменении isTopMostWithContainFocus
 *
 * @returns JSX заглушка
 */
export function TriggerFirstElementFocus({
    shouldFocusFirstElement,
    nodesRef,
    autoFocus,
}: {
    shouldFocusFirstElement?: boolean;
    nodesRef: RefObject<Element[]>;
    autoFocus: boolean;
}) {
    const autoFocusRef = useRef(autoFocus);
    const document = useMemo(() => getDocumentBody()?.ownerDocument, []);

    useEffect(() => {
        if (shouldFocusFirstElement) {
            const activeElement = document?.activeElement;
            // Пропускаем фокусировку на первом элементе, если он внутри скоупа, и не является фоллбэчным элементом
            if (
                activeElement &&
                isElementInScope(activeElement, nodesRef.current) &&
                !(autoFocusRef.current && activeElement.hasAttribute('focus-fallback'))
            ) {
                return;
            }

            if (nodesRef.current) {
                focusFirstInScope({
                    scope: nodesRef.current,
                });
            }

            autoFocusRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps --- focusManager не должен попадать в deps
    }, [shouldFocusFirstElement]);

    return null;
}
